import React, { useRef, useState } from 'react';
import { ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { Tooltip, TooltipTrigger, TooltipContent } from '../../../components/ui/tooltip';
import { useAutoFitFontSize } from './useAutoFitFontSize';
import { formatCompactNumber } from '../../../utils/numberFormat';

const KPI_BIG_VALUE_MAX_PX = 48;
const KPI_BIG_VALUE_MIN_PX = 14;

// Shared presentational pieces for the "big value + comparison trend badge" KPI tiles
// (LiefertermintreueKPI, AggregationKPI, EfficiencyKPI, ObjectConversionKPI, ObjectCountKPI).
// Each tile keeps its own data fetching, trend computation and tooltip wrapper — only the
// layout skeleton and Tailwind classes live here.

export const TREND_BADGE_CLASSES = {
  positive: 'bg-success-50 text-success-700',
  negative: 'bg-error-50 text-error-700',
  neutral: 'bg-gray-100 text-gray-500',
};

const TREND_ICONS = { up: ArrowUpRight, down: ArrowDownRight, flat: Minus };

export const TrendArrowIcon = ({ direction }) => {
  const Icon = TREND_ICONS[direction] || Minus;
  return <Icon className="w-4 h-4" />;
};

// Outer row: a 2-row grid (row 1 = value/trend slots, row 2 = captions) + container-query so
// font sizes (clamp() below) resolve against this row's own width, not the viewport. Columns
// flow side by side and never wrap — see LiefertermintreueKPI.jsx for why an extra wrapped
// line breaks the fixed-height grid tile.
// The shared rows are what keep the two grey captions on exactly the same line: each
// KPIValueColumn is a subgrid of these rows, so row 1 is as tall as the TALLER of the two
// slots (e.g. an Ampel-colored value badge with its extra padding) and both captions start
// below it. With independent flex columns, one slot growing pushed only its own caption down.
// gap-y is rem-based (not cqw) so the number/caption spacing scales with browser zoom like the
// text itself does.
export const KPIValueRow = ({ children, className }) => (
  <div
    className={cn(
      'w-full min-w-0 grid grid-flow-col auto-cols-[minmax(0,1fr)] grid-rows-[auto_auto] justify-center gap-y-2 [container-type:inline-size] gap-x-[clamp(0.75rem,8cqw,2rem)]',
      className
    )}
  >
    {children}
  </div>
);

// One column of KPIValueRow (the value column, or the trend column), spanning both of its
// rows via subgrid. Columns share the row's width equally (auto-cols minmax(0,1fr)); `grow`
// is kept for API compatibility with the previous flex layout and has no extra effect.
export const KPIValueColumn = ({ children, className }) => (
  <div className={cn('min-w-0 row-span-2 grid grid-rows-subgrid justify-items-center text-center', className)}>
    {children}
  </div>
);

// min-h (not a fixed h) so a value rendered as a padded Ampel badge grows the slot instead of
// overflowing into the caption below; the subgrid row keeps both columns' slots equally tall.
// items-baseline for the value (so an optional unit suffix lines up on the number's
// baseline), items-center for the trend slot.
// forwardRef + ...rest are load-bearing: callers wrap the slots in HoverInfoCard, whose Radix
// `TooltipTrigger asChild` injects a ref and pointer/focus handlers into this element. Dropping
// them means the hover tooltip never opens.
export const KPIValueSlot = React.forwardRef(({ children, className, ...rest }, ref) => (
  <div ref={ref} {...rest} className={cn('min-h-12 w-full min-w-0 flex items-baseline justify-center', className)}>{children}</div>
));
KPIValueSlot.displayName = 'KPIValueSlot';

export const KPITrendSlot = React.forwardRef(({ children, className, ...rest }, ref) => (
  <div ref={ref} {...rest} className={cn('min-h-12 w-full min-w-0 flex items-center justify-center', className)}>{children}</div>
));
KPITrendSlot.displayName = 'KPITrendSlot';

// The big number itself. `leading-none` is load-bearing: without it, the line box inherits
// the global `html { line-height: 1.5 }` (globals.css), which at this span's larger clamp()
// sizes is taller than the fixed h-12 slot above and overflows into whatever sits below it.
// `inline-block` is load-bearing too: some callers (EfficiencyKPI/ObjectConversionKPI/
// ObjectCountKPI's Ampel threshold coloring) add vertical padding + rounded corners to turn
// this into a small badge — a plain inline span renders that padding without it affecting
// the line box, so the badge background can visually bleed into the row above/below.
// `className`/`style` let callers override the default blue for that Ampel case.
//
// Font size auto-fits the value to its container via useAutoFitFontSize instead of the previous
// static clamp() — a plain `truncate`-to-ellipsis was clipping large numbers (6+ digits) once the
// clamp() floor no longer fit. `truncate` stays on the span only as a last-resort safety net for
// the rare case even the abbreviated (compact) form wouldn't fit. Pass `rawValue` (the underlying
// number) to enable the compact fallback ("1,2 Mio.") once shrinking bottoms out; omit it for
// values that don't make sense abbreviated (percentages, durations).
export const KPIBigValue = ({ children, className, style, rawValue }) => {
  const text = typeof children === 'string' || typeof children === 'number' ? String(children) : '';
  const compactText = rawValue != null ? formatCompactNumber(rawValue) : undefined;
  const { ref, fontSizePx, displayText, isCompact } = useAutoFitFontSize({
    text,
    maxPx: KPI_BIG_VALUE_MAX_PX,
    minPx: KPI_BIG_VALUE_MIN_PX,
    compactText,
  });

  return (
    <span
      ref={ref}
      className={cn(
        'inline-block max-w-full truncate font-bold tracking-tight leading-none text-primary-600',
        className
      )}
      style={{ ...style, fontSize: text ? `${fontSizePx}px` : undefined }}
      title={isCompact ? text : undefined}
    >
      {text ? displayText : children}
    </span>
  );
};

// Small grey caption line (date range, "vs. last year", "no comparison value" fallback).
// At high browser zoom / narrow tiles the clamp() floor can still be wider than the text,
// so `truncate` clips real information (e.g. half a date). The caption is therefore always
// a tooltip trigger, but the tooltip only opens if the text is actually clipped — measured
// at hover time, so it's always current for the zoom level/tile width at that moment.
// The tree is deliberately identical whether clipped or not: conditionally wrapping the div
// in <Tooltip> remounts it, which detached the node a ResizeObserver was watching and
// flipped the tooltip straight back off. Relies on being rendered inside one of the tile's
// existing <TooltipProvider> wrappers.
export const KPICaption = ({ children, className }) => {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);

  const handleOpenChange = (next) => {
    const el = ref.current;
    setOpen(next && !!el && el.scrollWidth > el.clientWidth + 1);
  };

  return (
    <Tooltip open={open} onOpenChange={handleOpenChange}>
      <TooltipTrigger asChild>
        <div
          ref={ref}
          className={cn(
            'w-full min-w-0 truncate leading-tight text-[clamp(0.625rem,3cqw,0.75rem)] text-muted-foreground',
            className
          )}
        >
          {children}
        </div>
      </TooltipTrigger>
      <TooltipContent>{children}</TooltipContent>
    </Tooltip>
  );
};

// The comparison pill (arrow icon + formatted delta), colored via TREND_BADGE_CLASSES.
export const KPITrendBadge = ({ trend, children, className }) => (
  <div
    className={cn(
      'min-w-0 max-w-full inline-flex items-center gap-1 px-2.5 py-1 rounded-full leading-none text-[clamp(0.7rem,3.5cqw,0.875rem)] font-semibold',
      TREND_BADGE_CLASSES[trend.color],
      className
    )}
  >
    <TrendArrowIcon direction={trend.direction} />
    <span className="min-w-0 truncate">{children}</span>
  </div>
);
